

























































import { computed, defineComponent } from "@vue/composition-api";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useDarkModeController from "@/hooks/useDarkModeController";
import { darkModeReplacePostfix } from "@/lib/darkModeReplacePostfix";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import BaseIcon from "@/components/Base/BaseIcon";
import useProfileTagsVisibility from "./hooks/useProfileTagsVisibility";

interface Social {
  id: string;
  href: string;
  iconName: string;
}
interface Info {
  id: string;
  text: string;
  class: string;
  tag: string;
  icon?: string;
  attrs: Record<string, string>;
}

export default defineComponent({
  name: "QuestionaryInfoPrinter",
  components: { BaseIcon },
  props: {
    questionaryInfo: { type: Object as () => Record<string, string>, required: true },
    user: { type: Object as () => UserPublicData, required: true },
    personLocation: { type: String, default: "" },
    profileTags: { type: Array as () => string[], default: () => [] },
  },
  setup(props) {
    const { isDarkMode } = useDarkModeController();
    const {
      profileTagsContainer,
      showAllProfileTags,
      visibleProfileTags,
      hiddenProfileTagsCount,
      toggleProfileTags,
    } = useProfileTagsVisibility(() => props.profileTags);

    const availableInfos = computed((): Info[] => {
      const infoItems = getQuestionaryInfoFields()
        .filter(
          i => !i.fieldMeta?.social && !i.fieldMeta?.amazon && !i.fieldMeta?.calendar && props.questionaryInfo[i.name]
        )
        .map(infoField => {
          const classes = [
            ...(infoField.fieldMeta?.description ? ["caption mb-3"] : []),
            ...(infoField.fieldMeta?.url ? ["questionary-info__link link mb-3"] : []),
          ];
          let text = props.questionaryInfo[infoField.name];
          if (infoField.fieldMeta?.url) {
            text = text.replace(/(^\w+:|^)\/\//, "");
          }
          return {
            id: infoField.name,
            text,
            class: classes.join(" "),
            tag: infoField.fieldMeta?.url ? "a" : "div",
            attrs: {
              ...(infoField.fieldMeta?.url ? { href: props.questionaryInfo[infoField.name], target: "_blank" } : {}),
            },
            ...(infoField.fieldMeta?.icon ? { icon: infoField.fieldMeta?.icon } : {}),
          };
        });

      if (!props.personLocation) {
        return infoItems;
      }

      const personLocationInfo: Info = {
        id: "personLocation",
        text: props.personLocation,
        class: "caption mb-3 gray-text",
        tag: "div",
        icon: "sci-map-marker",
        attrs: {},
      };
      const aboutIndex = infoItems.findIndex(i => i.id === "about");
      if (aboutIndex >= 0) {
        infoItems.splice(aboutIndex + 1, 0, personLocationInfo);
      } else {
        infoItems.unshift(personLocationInfo);
      }

      return infoItems;
    });

    const availableSocials = computed(() => {
      return getQuestionaryInfoFields()
        .filter(i => i.fieldMeta?.social || i.fieldMeta?.calendar)
        .reduce((out, socialField) => {
          if (props.questionaryInfo[socialField.name])
            out.push({
              id: socialField.name,
              href: `${socialField.fieldMeta?.site ?? ""}${props.questionaryInfo[socialField.name]}`,
              iconName: darkModeReplacePostfix(socialField.appendIcon ?? "", isDarkMode.value),
            });
          return out;
        }, [] as Social[]);
    });

    const availableAmazonLinks = computed(() => {
      return getQuestionaryInfoFields()
        .filter(i => i.fieldMeta?.amazon)
        .reduce((out, amazonField) => {
          if (props.questionaryInfo[amazonField.name])
            out.push({
              id: amazonField.name,
              href: props.questionaryInfo[amazonField.name],
              iconName: darkModeReplacePostfix(amazonField.fieldMeta?.logo ?? "", isDarkMode.value),
            });
          return out;
        }, [] as Social[]);
    });
    return {
      isDarkMode,
      availableInfos,
      availableSocials,
      availableAmazonLinks,
      profileTagsContainer,
      showAllProfileTags,
      visibleProfileTags,
      hiddenProfileTagsCount,
      toggleProfileTags,
    };
  },
});
